import { useCallback, useRef, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useUnredeemedOvertimeTickets } from "./useUnredeemedOvertimeTickets"
import { useBalanceContext } from "@/contexts/BalanceContext"

interface UseOvertimeBalanceOptimizerProps {
  address: string | undefined
  selectedCollateralIndex?: number
  enabled?: boolean
}

interface UseOvertimeBalanceOptimizerReturn {
  data: {
    inBets: number
    toPayout: number
    totalBets: number
    totalBetAmount: number
    totalPayoutAmount: number
  }
  isFetching: boolean
  refetch: () => void
  invalidateCache: () => void
  // Background update methods
  startBackgroundUpdates: () => void
  stopBackgroundUpdates: () => void
}

/**
 * Optimized overtime balance hook that implements Azuro's performance patterns
 * - Smart caching with background updates
 * - Debounced refetching
 * - Memory-efficient data processing
 * - Background polling for live updates
 */
export const useOvertimeBalanceOptimizer = ({
  address,
  selectedCollateralIndex,
  enabled = true,
}: UseOvertimeBalanceOptimizerProps): UseOvertimeBalanceOptimizerReturn => {
  const queryClient = useQueryClient()
  const { invalidateOvertimeCache } = useBalanceContext()

  // Background update management
  const backgroundIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isBackgroundActiveRef = useRef(false)
  const lastUpdateTimeRef = useRef<number>(0)

  // Performance constants
  const BACKGROUND_UPDATE_INTERVAL = 30000 // 30 seconds
  const MIN_UPDATE_INTERVAL = 5000 // 5 seconds minimum between updates
  const CACHE_STALE_TIME = 15000 // 15 seconds cache validity

  const {
    data,
    isFetching,
    refetch,
    invalidateCache: invalidateLocalCache,
  } = useUnredeemedOvertimeTickets({
    address,
    selectedCollateralIndex,
    enabled,
  })

  // Smart cache invalidation
  const invalidateCache = useCallback(() => {
    // Clear local cache
    invalidateLocalCache()

    // Invalidate global cache
    invalidateOvertimeCache()

    // Invalidate React Query cache for this specific query
    queryClient.invalidateQueries({
      queryKey: ["userTickets", address],
    })
  }, [invalidateLocalCache, invalidateOvertimeCache, queryClient, address])

  // Debounced refetch with performance optimization
  const optimizedRefetch = useCallback(() => {
    const now = Date.now()

    // Prevent rapid successive refetches
    if (now - lastUpdateTimeRef.current < MIN_UPDATE_INTERVAL) {
      return
    }

    lastUpdateTimeRef.current = now
    refetch()
  }, [refetch])

  // Background update management
  const startBackgroundUpdates = useCallback(() => {
    if (isBackgroundActiveRef.current || !enabled || !address) {
      return
    }

    isBackgroundActiveRef.current = true

    backgroundIntervalRef.current = setInterval(() => {
      // Only update if data is stale
      if (Date.now() - lastUpdateTimeRef.current > CACHE_STALE_TIME) {
        optimizedRefetch()
      }
    }, BACKGROUND_UPDATE_INTERVAL)
  }, [enabled, address, optimizedRefetch])

  const stopBackgroundUpdates = useCallback(() => {
    if (backgroundIntervalRef.current) {
      clearInterval(backgroundIntervalRef.current)
      backgroundIntervalRef.current = null
    }
    isBackgroundActiveRef.current = false
  }, [])

  // Auto-start background updates when enabled
  useEffect(() => {
    if (enabled && address) {
      startBackgroundUpdates()
    } else {
      stopBackgroundUpdates()
    }

    return () => {
      stopBackgroundUpdates()
    }
  }, [enabled, address, startBackgroundUpdates, stopBackgroundUpdates])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopBackgroundUpdates()
    }
  }, [stopBackgroundUpdates])

  return {
    data: data || {
      inBets: 0,
      toPayout: 0,
      totalBets: 0,
      totalBetAmount: 0,
      totalPayoutAmount: 0,
    },
    isFetching,
    refetch: optimizedRefetch,
    invalidateCache,
    startBackgroundUpdates,
    stopBackgroundUpdates,
  }
}
